import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import {
  FaCalendarAlt,
  FaCreditCard,
  FaRupeeSign,
  FaSave,
  FaTag,
  FaUser,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/class-settings-modals.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const PAYMENT_MODES = [
  "Cash",
  "Cheque",
  "Bank Transfer",
  "UPI",
  "Card",
  "Other",
];

const emptyForm = (defaultDate) => ({
  entry_type: "CASH_OUT",
  date: defaultDate || new Date().toISOString().slice(0, 10),
  category: "",
  amount: "",
  party: "",
  payment_mode: "Cash",
  reference_no: "",
  remark: "",
  status: "APPROVED",
});

//Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

//Created By Cursor
function toInputDate(raw) {
  const d = parseLooseDate(raw);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

//Created By Cursor
function clampDateToRange(isoDate, minIso, maxIso) {
  if (!isoDate) return isoDate;
  if (minIso && isoDate < minIso) return minIso;
  if (maxIso && isoDate > maxIso) return maxIso;
  return isoDate;
}

//Created By Cursor
const MdlAddExpense = ({
  isOpen,
  onClose,
  sessionId,
  sessionStartDate,
  sessionEndDate,
  onSuccess,
}) => {
  const [newExpense, setNewExpense] = useState(emptyForm);
  const [expenseList, setExpenseList] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [partyInput, setPartyInput] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [partyId, setPartyId] = useState("");
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [showPartyInput, setShowPartyInput] = useState(false);

  //Created By Cursor
  const fetchCategoriesAndParties = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;

    try {
      setLoadingMeta(true);
      const response = await axios.get(`${API_BASE()}getDaybookAddExpenseData.php`, {
        params: { school_code: schoolCode },
      });
      if (response.data?.api_status === "OK") {
        const data = response.data.data || {};
        setCategoryList(data.DaybookCategory || []);
        setPartyList(data.DaybookParty || []);
      }
    } catch (error) {
      console.error("Error fetching categories and parties:", error);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  //Created By Cursor
  const fetchRecentExpenses = useCallback(
    async (entryType = "CASH_OUT") => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!sessionId || !schoolCode) return;

      try {
        setLoadingExpenses(true);
        const response = await axios.get(
          `${API_BASE()}getDaybookExpenseList.php`,
          {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              limit: 1,
              offset: 0,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          const raw = response.data.data || [];
          const filtered = raw.filter(
            (item) =>
              String(item.entry_type || "").toUpperCase() ===
              String(entryType).toUpperCase()
          );
          setExpenseList(filtered);
        } else {
          setExpenseList([]);
        }
      } catch (error) {
        console.error("Error fetching recent expenses:", error);
        setExpenseList([]);
      } finally {
        setLoadingExpenses(false);
      }
    },
    [sessionId]
  );

  //Created By Cursor — resolve session date bounds (props or CurrentSession)
  const getSessionBounds = useCallback(() => {
    const fromSession = SessionManager.getCurrentSession()?.[0];
    const minIso =
      toInputDate(sessionStartDate) ||
      toInputDate(fromSession?.start_date) ||
      "";
    const maxIso =
      toInputDate(sessionEndDate) || toInputDate(fromSession?.end_date) || "";
    return { minIso, maxIso };
  }, [sessionStartDate, sessionEndDate]);

  useEffect(() => {
    if (!isOpen) return;
    const { minIso, maxIso } = getSessionBounds();
    const today = new Date().toISOString().slice(0, 10);
    const safeDate = clampDateToRange(today, minIso, maxIso);
    setNewExpense(emptyForm(safeDate));
    fetchCategoriesAndParties();
    if (sessionId) {
      fetchRecentExpenses("CASH_OUT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId, fetchCategoriesAndParties, fetchRecentExpenses, getSessionBounds]);

  useEffect(() => {
    if (isOpen) return;
    setNewExpense(emptyForm());
    setCategoryInput("");
    setPartyInput("");
    setCategoryId("");
    setPartyId("");
    setShowCategoryInput(false);
    setShowPartyInput(false);
    setExpenseList([]);
    setSaving(false);
  }, [isOpen]);

  //Created By Cursor
  const handleExpenseChange = (e) => {
    const { name, value } = e.target;
    setNewExpense((prev) => ({ ...prev, [name]: value }));
  };

  //Created By Cursor
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "" || value === "__SELECT__") {
      setCategoryInput("");
      setCategoryId("");
      setShowCategoryInput(false);
      setNewExpense((prev) => ({ ...prev, category: "" }));
    } else if (value === "__NEW__") {
      setCategoryInput("");
      setCategoryId("");
      setShowCategoryInput(true);
      setNewExpense((prev) => ({ ...prev, category: "" }));
    } else {
      const selected = categoryList.find((cat) => cat.category_id === value);
      if (selected) {
        setCategoryId(selected.category_id);
        setCategoryInput(selected.category_name);
        setShowCategoryInput(false);
        setNewExpense((prev) => ({
          ...prev,
          category: selected.category_name,
        }));
      }
    }
  };

  //Created By Cursor
  const handleCategoryInputChange = (e) => {
    const value = e.target.value;
    setCategoryInput(value);
    setCategoryId("");
    setNewExpense((prev) => ({ ...prev, category: value }));
  };

  //Created By Cursor
  const handlePartyChange = (e) => {
    const value = e.target.value;
    if (value === "" || value === "__SELECT__") {
      setPartyInput("");
      setPartyId("");
      setShowPartyInput(false);
      setNewExpense((prev) => ({ ...prev, party: "" }));
    } else if (value === "__NEW__") {
      setPartyInput("");
      setPartyId("");
      setShowPartyInput(true);
      setNewExpense((prev) => ({ ...prev, party: "" }));
    } else {
      const selected = partyList.find((party) => party.party_id === value);
      if (selected) {
        setPartyId(selected.party_id);
        setPartyInput(selected.party_name);
        setShowPartyInput(false);
        setNewExpense((prev) => ({ ...prev, party: selected.party_name }));
      }
    }
  };

  //Created By Cursor
  const handlePartyInputChange = (e) => {
    const value = e.target.value;
    setPartyInput(value);
    setPartyId("");
    setNewExpense((prev) => ({ ...prev, party: value }));
  };

  //Created By Cursor
  const handleSaveExpense = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const schoolCode = SessionManager.getSchoolCode();
    if (!sessionId) {
      myToast.notify_warning("Please select session before adding expense.");
      return;
    }
    if (!schoolCode) {
      myToast.notify_warning("School code missing. Please login again.");
      return;
    }

    const staffDetails = SessionManager.getStaffDetails();
    const staff_id = staffDetails?.staff_id || "";
    const login_type = staffDetails?.login_type || "";

    let status = "PENDING";
    let approved_by = "";
    if (login_type === "Admin") {
      status = "APPROVED";
      approved_by = staff_id;
    }

    const {
      date,
      amount,
      category,
      party,
      payment_mode,
      reference_no,
      remark,
    } = newExpense;

    if (!date || !amount) {
      myToast.notify_warning("Please fill date and amount.");
      return;
    }

    const { minIso, maxIso } = getSessionBounds();
    if (minIso && date < minIso) {
      myToast.notify_warning(
        `Date cannot be before session start date (${minIso}).`
      );
      return;
    }
    if (maxIso && date > maxIso) {
      myToast.notify_warning(
        `Date cannot be after session end date (${maxIso}).`
      );
      return;
    }

    if (!category || category.trim() === "") {
      myToast.notify_warning("Please select or enter a category.");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.get(`${API_BASE()}addExpenseEntry.php`, {
        params: {
          session_id: sessionId,
          amount: String(amount),
          entry_date: date,
          category_id: categoryId || "",
          category_name: category || "",
          party_id: partyId || "",
          party_name: party || "",
          payment_mode: payment_mode || "Cash",
          reference_no: reference_no || "",
          remark: remark || "",
          entry_type: "CASH_OUT",
          school_code: schoolCode,
          added_by: staff_id,
          staff_id: staff_id,
          status: status,
          approved_by: approved_by,
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Expense added successfully.");
        await fetchRecentExpenses("CASH_OUT");
        if (typeof onSuccess === "function") onSuccess();

        setNewExpense(
          emptyForm(
            clampDateToRange(
              new Date().toISOString().slice(0, 10),
              minIso,
              maxIso
            )
          )
        );
        setCategoryInput("");
        setPartyInput("");
        setCategoryId("");
        setPartyId("");
        setShowCategoryInput(false);
        setShowPartyInput(false);
      } else if (response.data?.api_remark) {
        myToast.notify_warning(response.data.api_remark);
      } else {
        myToast.notify_warning("Failed to add expense. Please try again.");
      }
    } catch (error) {
      console.error("Error adding expense entry:", error);
      myToast.notify_warning("Server error while adding expense.");
    } finally {
      setSaving(false);
    }
  };

  const sessionBounds = getSessionBounds();

  return (
    <Modal
      show={Boolean(isOpen)}
      onHide={onClose}
      animation={false}
      size="lg"
      centered
      dialogClassName="cls-mdl-root em-mdl-root"
      aria-labelledby="add-expense-title"
    >
      <Modal.Header closeButton>
        <div className="em-mdl-header-row">
          <div className="em-mdl-header-brand">
            <span className="em-mdl-header-icon" aria-hidden>
              <FaCreditCard />
            </span>
            <div>
              <Modal.Title id="add-expense-title">Add Expense</Modal.Title>
              <span className="cls-mdl-sub">
                Cash-out entry for the selected session
              </span>
            </div>
          </div>
          <button
            type="button"
            className="em-mdl-header-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loadingMeta && categoryList.length === 0 ? (
          <MyLoader />
        ) : (
          <form id="em-add-expense-form" onSubmit={handleSaveExpense}>
            <div className="em-mdl-section">
              <div className="em-mdl-section__head">
                <span>01</span>
                <div>
                  <strong>Expense details</strong>
                  <em>Date, amount, category and party</em>
                </div>
              </div>
              <div className="cls-mdl-panel em-mdl-panel">
                <div className="cls-mdl-grid">
                  <label className="cls-mdl-field">
                    <span>
                      <FaCalendarAlt aria-hidden /> Date *
                      {(sessionBounds.minIso || sessionBounds.maxIso) && (
                        <em className="em-mdl-date-range">
                          {" "}
                          ({sessionBounds.minIso || "—"} →{" "}
                          {sessionBounds.maxIso || "—"})
                        </em>
                      )}
                    </span>
                    <input
                      type="date"
                      name="date"
                      value={newExpense.date}
                      onChange={handleExpenseChange}
                      min={sessionBounds.minIso || undefined}
                      max={sessionBounds.maxIso || undefined}
                      required
                      disabled={saving}
                    />
                  </label>

                  <label className="cls-mdl-field">
                    <span>
                      <FaRupeeSign aria-hidden /> Amount *
                    </span>
                    <div className="em-mdl-amount">
                      <span className="em-mdl-amount__prefix" aria-hidden>
                        ₹
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="amount"
                        placeholder="0.00"
                        value={newExpense.amount}
                        onChange={handleExpenseChange}
                        required
                        disabled={saving}
                      />
                    </div>
                  </label>

                  <label className="cls-mdl-field">
                    <span>
                      <FaTag aria-hidden /> Category *
                    </span>
                    <select
                      value={categoryId || (showCategoryInput ? "__NEW__" : "")}
                      onChange={handleCategoryChange}
                      disabled={saving}
                    >
                      <option value="">Select Category</option>
                      {categoryList.map((cat) => (
                        <option key={cat.category_id} value={cat.category_id}>
                          {cat.category_name}
                        </option>
                      ))}
                      <option value="__NEW__">+ Add New Category</option>
                    </select>
                  </label>

                  <label className="cls-mdl-field">
                    <span>
                      <FaUser aria-hidden /> Party
                    </span>
                    <select
                      value={partyId || (showPartyInput ? "__NEW__" : "")}
                      onChange={handlePartyChange}
                      disabled={saving}
                    >
                      <option value="">Select Party</option>
                      {partyList.map((p) => (
                        <option key={p.party_id} value={p.party_id}>
                          {p.party_name}
                        </option>
                      ))}
                      <option value="__NEW__">+ Add New Party</option>
                    </select>
                  </label>

                  {showCategoryInput ? (
                    <label className="cls-mdl-field full">
                      <span>New category name *</span>
                      <input
                        type="text"
                        placeholder="Enter category"
                        value={categoryInput}
                        onChange={handleCategoryInputChange}
                        required
                        disabled={saving}
                      />
                    </label>
                  ) : null}

                  {showPartyInput ? (
                    <label className="cls-mdl-field full">
                      <span>New party name</span>
                      <input
                        type="text"
                        placeholder="Enter party"
                        value={partyInput}
                        onChange={handlePartyInputChange}
                        disabled={saving}
                      />
                    </label>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="em-mdl-section em-mdl-section--compact">
              <div className="em-mdl-section__head">
                <span>02</span>
                <div>
                  <strong>Payment</strong>
                  <em>Mode, reference and notes</em>
                </div>
              </div>
              <div className="cls-mdl-panel em-mdl-panel em-mdl-panel--compact">
                <div className="em-mdl-pay-compact">
                  <div className="cls-mdl-field em-mdl-pay-field">
                    <span>Mode</span>
                    <div
                      className="em-mdl-pay-chips"
                      role="group"
                      aria-label="Payment mode"
                    >
                      {PAYMENT_MODES.map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          className={`em-mdl-pay-chip${
                            newExpense.payment_mode === mode ? " is-active" : ""
                          }`}
                          onClick={() =>
                            setNewExpense((prev) => ({
                              ...prev,
                              payment_mode: mode,
                            }))
                          }
                          disabled={saving}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="cls-mdl-field">
                    <span>Reference</span>
                    <input
                      type="text"
                      name="reference_no"
                      placeholder="Cheque / UPI / Txn ID"
                      value={newExpense.reference_no}
                      onChange={handleExpenseChange}
                      disabled={saving}
                    />
                  </label>

                  <label className="cls-mdl-field">
                    <span>Notes</span>
                    <input
                      type="text"
                      name="remark"
                      placeholder="Optional notes"
                      value={newExpense.remark}
                      onChange={handleExpenseChange}
                      disabled={saving}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="em-mdl-recent">
              <div className="em-mdl-recent__head">
                <h6>Last Entry</h6>
                <span>{expenseList.length > 0 ? 1 : 0}</span>
              </div>
              <div className="em-mdl-recent__list">
                {loadingExpenses ? (
                  <div className="em-mdl-recent__empty">Loading…</div>
                ) : expenseList.length === 0 ? (
                  <div className="em-mdl-recent__empty">No entries yet</div>
                ) : (
                  <article className="em-mdl-recent__item">
                    <div className="em-mdl-recent__item-main">
                      <strong>{expenseList[0].category_name || "—"}</strong>
                      <span>
                        {expenseList[0].entry_date || "—"}
                        {expenseList[0].party_name
                          ? ` · ${expenseList[0].party_name}`
                          : ""}
                      </span>
                      {expenseList[0].remark ? (
                        <em>{expenseList[0].remark}</em>
                      ) : null}
                    </div>
                    <div className="em-mdl-recent__item-amt">
                      <strong>₹ {expenseList[0].amount}</strong>
                      <span className="em-mdl-pill em-mdl-pill--out">
                        Expense
                      </span>
                    </div>
                  </article>
                )}
              </div>
            </div>
          </form>
        )}
      </Modal.Body>

      <Modal.Footer className="em-mdl-footer">
        <Button
          className="cls-mdl-btn-ghost"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          className="em-mdl-save"
          type="submit"
          form="em-add-expense-form"
          disabled={saving || loadingMeta}
        >
          <span className="em-mdl-save__icon" aria-hidden>
            <FaSave />
          </span>
          <span>{saving ? "Saving…" : "Save Expense"}</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlAddExpense;
